// client/components/FixoriumChart.tsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, IChartApi, CandlestickData, Time, ColorType } from 'lightweight-charts';
import { getFixoriumChartData, getCurrentTokenData, TimeFrame, CandleData } from '@/lib/fixorium-chart';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';

interface FixoriumChartProps {
  tokenAddress: string;
  tokenSymbol: string;
  tokenName?: string;
  maxTimeframe?: string;
}

const TIMEFRAMES: { label: string; value: TimeFrame }[] = [
  { label: '1H', value: '1H' },
  { label: '4H', value: '4H' },
  { label: '1D', value: '1D' },
  { label: '1W', value: '1W' },
  { label: '1M', value: '1M' },
  { label: '3M', value: '3M' },
  { label: '4M', value: '4M' },
];

export default function FixoriumChart({ tokenAddress, tokenSymbol, tokenName }: FixoriumChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [candles, setCandles] = useState<CandleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<TimeFrame>('1D');
  const [error, setError] = useState<string | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [priceChange, setPriceChange] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchChartData = useCallback(async () => {
    if (!tokenAddress) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getFixoriumChartData(tokenAddress, timeframe);
      setCandles(data);
      
      if (data.length > 0) {
        setCurrentPrice(data[data.length - 1].close);
      }
      
      const currentData = await getCurrentTokenData(tokenAddress);
      if (currentData) {
        setPriceChange(currentData.priceChange24h);
        if (currentData.price && !currentPrice) {
          setCurrentPrice(currentData.price);
        }
      }
    } catch (err) {
      console.error('Failed to fetch chart data:', err);
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, [tokenAddress, timeframe]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  useEffect(() => {
    if (!chartContainerRef.current || candles.length === 0) return;
    
    if (chartRef.current) {
      chartRef.current.remove();
    }
    
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0f172a' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#3b82f6',
          width: 1,
          style: 1,
        },
        horzLine: {
          color: '#3b82f6',
          width: 1,
          style: 1,
        },
      },
      rightPriceScale: {
        borderColor: '#334155',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: '#334155',
        timeVisible: true,
        secondsVisible: false,
      },
      width: chartContainerRef.current.clientWidth,
      height: isFullscreen ? window.innerHeight - 100 : 450,
    });
    
    chartRef.current = chart;
    
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderDownColor: '#ef4444',
      borderUpColor: '#22c55e',
      wickDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      borderVisible: true,
    });
    
    const chartData: CandlestickData<Time>[] = candles.map(candle => ({
      time: candle.time as Time,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }));
    
    candlestickSeries.setData(chartData);
    
    const volumeSeries = chart.addHistogramSeries({
      color: '#3b82f6',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: 'volume',
    });
    
    const volumeData = candles.map(candle => ({
      time: candle.time as Time,
      value: candle.volume,
      color: candle.close >= candle.open ? '#22c55e80' : '#ef444480',
    }));
    
    volumeSeries.setData(volumeData);
    chart.timeScale().fitContent();
    
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [candles, timeframe, isFullscreen]);

  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: isFullscreen ? window.innerHeight - 100 : 450,
        });
        chartRef.current.timeScale().fitContent();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current?.clientWidth || 0,
          height: isFullscreen ? 450 : window.innerHeight - 100,
        });
        chartRef.current.timeScale().fitContent();
      }
    }, 100);
  };

  const priceChangeColor = priceChange >= 0 ? 'text-green-500' : 'text-red-500';
  const priceChangeSymbol = priceChange >= 0 ? '+' : '';

  return (
    <div className={`bg-slate-900 rounded-lg ${isFullscreen ? 'fixed inset-0 z-50 p-4' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-slate-800">
        <div>
          <h2 className="text-xl font-bold text-white">
            {tokenSymbol} {tokenName && <span className="text-sm text-gray-400">/ USD</span>}
          </h2>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-2xl font-bold text-white">
              ${currentPrice.toFixed(currentPrice < 1 ? 6 : 2)}
            </span>
            <span className={`text-sm font-semibold ${priceChangeColor}`}>
              {priceChangeSymbol}{priceChange.toFixed(2)}%
            </span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-1">
          {TIMEFRAMES.map((tf) => (
            <Button
              key={tf.value}
              variant={timeframe === tf.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeframe(tf.value)}
              className={`text-xs h-8 px-3 ${
                timeframe === tf.value 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'border-slate-700 text-gray-400 hover:text-white'
              }`}
            >
              {tf.label}
            </Button>
          ))}
        </div>
        
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchChartData}
            disabled={loading}
            className="h-8 px-2 border-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="h-8 px-2 border-slate-700"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      
      <div className="relative p-4">
        {loading ? (
          <div className="flex items-center justify-center" style={{ height: isFullscreen ? 'calc(100vh - 120px)' : 450 }}>
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center" style={{ height: isFullscreen ? 'calc(100vh - 120px)' : 450 }}>
            <div className="text-center">
              <p className="text-red-400 mb-2">{error}</p>
              <Button onClick={fetchChartData} variant="outline" size="sm">
                Try Again
              </Button>
            </div>
          </div>
        ) : candles.length === 0 ? (
          <div className="flex items-center justify-center" style={{ height: isFullscreen ? 'calc(100vh - 120px)' : 450 }}>
            <p className="text-gray-400">No chart data available for {tokenSymbol}</p>
          </div>
        ) : (
          <div 
            ref={chartContainerRef} 
            className="w-full"
            style={{ height: isFullscreen ? 'calc(100vh - 120px)' : 450 }}
          />
        )}
      </div>
      
      <div className="px-4 pb-4 text-xs text-gray-500">
        Data provided by DexScreener • Real-time OHLCV data
      </div>
    </div>
  );
}
